import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CTABanner from "../components/CTABanner";
import ExpandingPresence from "../components/projects/ExpandingPresence";
import { PROJECTS } from "../data/products";
import { motion } from "framer-motion";
import { MapPin, ArrowUpRight } from "lucide-react";
import Seo, { buildBreadcrumb } from "../components/Seo";
import { PAGE_SEO } from "../data/seo";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: "easeOut" } },
};

/* ---------------- Project Card ---------------- */
function ProjectCard({ project, index }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease: "easeOut", delay: (index % 3) * 0.08 }}
      data-testid={`project-card-${index}`}
      className="group relative overflow-hidden rounded-2xl bg-[#101010] shadow-[0_10px_28px_-12px_rgba(0,0,0,0.55)] hover:shadow-[0_28px_60px_-18px_rgba(255,107,26,0.45)] transition-shadow duration-500"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        <img
          src={project.image}
          alt={`${project.name} — ${project.city}, ${project.state}`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.08]"
        />
        {/* Bottom gradient (for label legibility) */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-liftile-black via-liftile-black/40 to-transparent"
        />
        {/* Dark overlay (only on hover) */}
        <div
          aria-hidden
          className="absolute inset-0 bg-liftile-black/0 group-hover:bg-liftile-black/40 transition-colors duration-500"
        />
        {/* Category eyebrow chip — top left */}
        <div className="absolute top-4 left-4">
          <span
            data-testid={`project-card-${index}-category`}
            className="inline-flex items-center font-heading text-[10px] uppercase tracking-[0.22em] text-liftile-orange bg-liftile-black/65 border border-liftile-orange/45 backdrop-blur-sm px-3 py-1.5 rounded-sm"
          >
            {project.category}
          </span>
        </div>
        {/* External-link icon — top right, reveal on hover */}
        <div
          aria-hidden
          className="absolute top-4 right-4 grid place-items-center w-10 h-10 rounded-full bg-liftile-orange text-white opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-400 shadow-lg"
        >
          <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
        </div>
      </div>

      {/* Body */}
      <div className="relative px-6 py-5 md:px-7 md:py-6 border-t border-white/[0.04]">
        <div className="w-10 h-0.5 bg-liftile-orange mb-3 transition-all duration-500 group-hover:w-16" />
        <h3
          data-testid={`project-card-${index}-name`}
          className="font-display uppercase text-lg md:text-xl text-liftile-cream tracking-tight leading-tight"
        >
          {project.name}
        </h3>

        <div className="mt-3 flex items-center gap-2 text-liftile-cream/65">
          <MapPin className="w-3.5 h-3.5 text-liftile-orange shrink-0" strokeWidth={2.4} />
          <span
            data-testid={`project-card-${index}-location`}
            className="font-body text-xs md:text-sm leading-none"
          >
            {project.city}, {project.state}
          </span>
        </div>
      </div>
    </motion.article>
  );
}

/* ---------------- Page ---------------- */
export default function ProjectsPage() {
  const seo = PAGE_SEO.projects;
  return (
    <>
      <Seo
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        image={seo.image}
        jsonLd={[
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Projects", path: "/projects" },
          ]),
        ]}
        jsonLdId="projects"
      />
      <Navbar />
      <main>
        {/* ============== HERO ============== */}
        <section
          data-testid="projects-hero"
          className="relative bg-liftile-black overflow-hidden pt-32 md:pt-40 pb-20 md:pb-24"
        >
          <div className="absolute inset-0 industrial-grid opacity-25" />
          {/* Ambient orange glow */}
          <div
            aria-hidden
            className="absolute -top-32 -left-32 w-[460px] h-[460px] rounded-full pointer-events-none opacity-25"
            style={{
              background:
                "radial-gradient(circle, rgba(255,107,26,0.5) 0%, rgba(255,107,26,0) 70%)",
              filter: "blur(40px)",
            }}
          />

          <div className="container-x relative">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="max-w-3xl"
            >
              <div className="heading-eyebrow mb-4">Our Work</div>
              <h1
                data-testid="projects-hero-headline"
                className="font-display uppercase text-liftile-cream leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
              >
                Projects That
                <br />
                <span className="text-liftile-orange">Define</span> Us
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6, ease: "easeOut" }}
                className="font-body text-liftile-cream/70 mt-7 text-base md:text-lg leading-relaxed max-w-2xl"
              >
                From residential towers and commercial sites to refineries and
                power plants — LIFTILE machinery is trusted on real construction
                projects across India.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* ============== GALLERY ============== */}
        <section
          data-testid="projects-gallery"
          className="bg-liftile-black pt-2 pb-20 md:pb-28"
        >
          <div className="container-x grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7 lg:gap-8">
            {PROJECTS.map((p, i) => (
              <ProjectCard key={p.image} project={p} index={i} />
            ))}
          </div>

          {/* Authenticity statement */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            data-testid="projects-authenticity"
            className="container-x mt-14 md:mt-20"
          >
            <div className="max-w-3xl mx-auto text-center px-5 relative">
              <div
                aria-hidden
                className="mx-auto w-14 h-px bg-liftile-orange mb-5"
              />
              <p className="font-body italic text-liftile-cream/70 text-sm md:text-base leading-relaxed">
                Every project showcased on this page represents a real LIFTILE
                machine{" "}
                <span className="text-liftile-cream not-italic font-semibold">
                  supplied or installed for actual customer projects.
                </span>
              </p>
            </div>
          </motion.div>
        </section>

        {/* ============== EXPANDING PRESENCE (replaces interactive city map) ============== */}
        <ExpandingPresence />
      </main>

      {/* ============== CTA ============== */}
      <CTABanner
        headline="Ready to Build with Confidence?"
        description="Whether you're constructing residential, commercial, industrial or infrastructure projects, our engineering team is ready to help you choose the right construction machinery."
      />

      <Footer />
    </>
  );
}
